import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { 
  getAllAdvertisements, 
  getAdvertisementsByCategory, 
  createAdvertisement, 
  updateAdvertisement, 
  deleteAdvertisement, 
  getAdvertisementsByPage
} from '../../api/advertisment/advertismentAPI';
import useAuthStore from '../../store/authStore';

// Hook to fetch all advertisements
export const useAdvertisements = () => {
  const { setError } = useAuthStore();

  const {
    data: advertisementsData,
    isLoading,
    error,
    refetch
  } = useQuery('advertisements', getAllAdvertisements, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch advertisements';
      setError(errorMessage);
    },
    retry: 1,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    advertisementsData,
    isLoading,
    error,
    refetchAdvertisements: refetch
  };
};

// Hook to fetch advertisements by category
export const useAdvertisementsByCategory = (category) => {
  const { setError } = useAuthStore();

  const {
    data: categoryAdvertisementsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['advertisementsByCategory', category], 
    () => getAdvertisementsByCategory(category), 
    {
      enabled: !!category,
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch advertisements by category';
        setError(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
    }
  );

  return {
    categoryAdvertisementsData,
    isLoading,
    error,
    refetchCategoryAdvertisements: refetch
  };
};

// Hook to fetch advertisements by category
export const useAdvertisementsByPage = (page) => {
    const { setError } = useAuthStore();
  
    const {
      data: categoryAdvertisementsData,
      isLoading,
      error,
      refetch
    } = useQuery(
      ['advertisementsByPage', page], 
      () => getAdvertisementsByPage(page), 
      {
        enabled: !!page,
        onError: (error) => {
          const errorMessage = error?.response?.data?.message || 'Failed to fetch advertisements by category';
          setError(errorMessage);
        },
        retry: 1,
        staleTime: 300000,
        cacheTime: 3600000,
      }
    );
  
    return {
      categoryAdvertisementsData,
      isLoading,
      error,
      refetchCategoryAdvertisements: refetch
    };
  };

// Hook for creating advertisements
export const useCreateAdvertisement = () => {
    const queryClient = useQueryClient();
    const { setError } = useAuthStore();
  
    const validateAdvertisementData = (data) => {
      // Convert FormData to a plain object if it's not already one
      const formData = data instanceof FormData
        ? Object.fromEntries(data.entries())
        : data;
  
      // Handle pages conversion
      if (typeof formData.pages === 'string') {
        // If pages is a comma-separated string, convert it to an object
        const pagesArray = formData.pages.split(',');
        formData.pages = {
          home: pagesArray.includes('home'),
          audio: pagesArray.includes('audio'),
          reading: pagesArray.includes('reading'),
          video: pagesArray.includes('video'),
          blogs: pagesArray.includes('blogs')
        };
      }
  
      const requiredFields = ['title', 'image_url', 'category', 'pages', 'start_date', 'end_date'];
  
      // Check for missing required fields
      const missingFields = requiredFields.filter(field => {
        // Special handling for pages
        if (field === 'pages') {
          return !formData.pages || Object.keys(formData.pages).length === 0;
        }
        return !formData[field];
      });
  
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }
  
      // Validate dates
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate < startDate) {
        throw new Error('End date must be greater than or equal to start date');
      }
  
      return formData;
    };
  
    const createAdvertisementMutation = useMutation(
      async (data) => {
        // Validate data first
        const validatedData = validateAdvertisementData(data);
  
        // If data is FormData, convert to JSON for API
        const payload = data instanceof FormData
          ? Object.fromEntries(data.entries())
          : validatedData;
  
        // Ensure pages is a JSON string
        payload.pages = typeof payload.pages === 'string'
          ? payload.pages
          : JSON.stringify(payload.pages);
  
        // Call API
        return createAdvertisement(payload);
      },
      {
        onSuccess: (response) => {
          toast.success("Advertisement created successfully!", {
            position: "top-right",
            autoClose: 3000,
          });
  
          // Invalidate and refetch advertisements queries
          queryClient.invalidateQueries('advertisements');
        },
        onError: (error) => {
          const errorMessage = error?.response?.data?.message || 'Failed to create advertisement';
          setError(errorMessage);
          toast.error(errorMessage);
        },
      }
    );
  
    return {
      createAdvertisement: createAdvertisementMutation.mutate,
      isLoading: createAdvertisementMutation.isLoading,
      isSuccess: createAdvertisementMutation.isSuccess,
      isError: createAdvertisementMutation.isError,
      error: createAdvertisementMutation.error,
      reset: createAdvertisementMutation.reset
    };
  };

// Hook for updating advertisements
export const useUpdateAdvertisement = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const updateAdvertisementMutation = useMutation(updateAdvertisement, {
    onSuccess: (response) => {
      toast.success("Advertisement updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Invalidate and refetch advertisements queries
      queryClient.invalidateQueries('advertisements');
      queryClient.invalidateQueries(['advertisementsByCategory', response.advertisement.category]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update advertisement';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    updateAdvertisement: updateAdvertisementMutation.mutate,
    isLoading: updateAdvertisementMutation.isLoading,
    isSuccess: updateAdvertisementMutation.isSuccess,
    isError: updateAdvertisementMutation.isError,
    error: updateAdvertisementMutation.error,
    reset: updateAdvertisementMutation.reset
  };
};

// Hook for deleting advertisements
export const useDeleteAdvertisement = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const deleteAdvertisementMutation = useMutation(deleteAdvertisement, {
    onSuccess: (response) => {
      toast.success("Advertisement deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Invalidate and refetch advertisements queries
      queryClient.invalidateQueries('advertisements');
      queryClient.invalidateQueries(['advertisementsByCategory', response.advertisement.category]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete advertisement';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    deleteAdvertisement: deleteAdvertisementMutation.mutate,
    isLoading: deleteAdvertisementMutation.isLoading,
    isSuccess: deleteAdvertisementMutation.isSuccess,
    isError: deleteAdvertisementMutation.isError,
    error: deleteAdvertisementMutation.error,
    reset: deleteAdvertisementMutation.reset
  };
};